import theme from '@gaia/theme';
import type {SharedData} from '@gaia/types';
import type {FeatureCollection, GeoJsonProperties, Geometry} from 'geojson';
import type mapboxgl from 'mapbox-gl';
import type {AnySourceData} from 'mapbox-gl';

import {defaultPinLayer} from './constants';
import {waypointFixedLayerIds} from './fixedLayerIds';

// When sharedData.type === 'FeatureCollection', shareData contains geometry for embedded map previews
// For embedded map previews, an alternate styling is used.

const regularLineStyle: mapboxgl.LineLayer[] = [
    {
        id: 'line',
        type: 'line',
        source: 'source',
        filter: [
            'has',
            ['geometry-type'],
            [
                'literal',
                {
                    Polygon: true,
                    MultiPolygon: true,
                    LineString: true,
                    MultiLineString: true,
                },
            ],
        ],
        paint: {
            'line-width': 4,
            'line-color': theme.palette.primary.main,
            'line-opacity': 0.75,
        },
    },
];
const embeddedMapStyle: mapboxgl.AnyLayer[] = [
    {
        id: 'casing',
        type: 'line',
        source: 'source',
        minzoom: 0,
        maxzoom: 24,
        filter: [
            'has',
            ['geometry-type'],
            [
                'literal',
                {
                    Polygon: true,
                    MultiPolygon: true,
                    LineString: true,
                    MultiLineString: true,
                },
            ],
        ],
        layout: {
            visibility: 'visible',
            'line-cap': 'round',
            'line-join': 'round',
        },
        paint: {
            'line-width': {
                stops: [
                    [11, 5],
                    [12, 6],
                    [13, 6],
                    [14, 8],
                    [16, 8],
                ],
            },
            'line-opacity': 1,
            'line-color': 'rgba(0, 0, 0, 1)',
            'line-offset': 0,
            'line-blur': 0,
        },
    },
    {
        id: 'highlight',
        type: 'line',
        source: 'source',
        minzoom: 0,
        maxzoom: 24,
        filter: [
            'has',
            ['geometry-type'],
            [
                'literal',
                {
                    Polygon: true,
                    MultiPolygon: true,
                    LineString: true,
                    MultiLineString: true,
                },
            ],
        ],
        layout: {
            visibility: 'visible',
            'line-cap': 'round',
            'line-join': 'round',
        },
        paint: {
            'line-width': {
                stops: [
                    [11, 2],
                    [12, 3],
                    [13, 3],
                    [14, 3],
                    [16, 3],
                ],
            },
            'line-opacity': 1,
            'line-color': 'rgba(168, 255, 63, 1)',
        },
    },
];

function getEmbeddedMapStyle(
    sharedData: FeatureCollection<Geometry, GeoJsonProperties>
): mapboxgl.AnyLayer[] {
    const isAnyWaypointSelected =
        sharedData.features.filter((f) => f.properties?.selected).length > 0;
    const opacityExpression = isAnyWaypointSelected ? ['case', ['get', 'selected'], 1, 0.2] : 1;
    return [
        ...embeddedMapStyle,
        {
            ...defaultPinLayer,
            id: waypointFixedLayerIds.embeddedMap,
            paint: {
                ...defaultPinLayer.paint,
                'icon-opacity': opacityExpression,
                'text-opacity': opacityExpression,
            },
        } as mapboxgl.AnyLayer,
    ];
}

export default function getSharedDataGeometryMapboxStyle(
    sharedData: SharedData
): mapboxgl.Style | undefined {
    if (!sharedData || (sharedData.type !== 'FeatureCollection' && sharedData.type !== 'geometry'))
        return undefined;

    const styleLayers =
        sharedData.type === 'FeatureCollection'
            ? getEmbeddedMapStyle(sharedData)
            : regularLineStyle;

    const source: AnySourceData =
        sharedData.type === 'FeatureCollection'
            ? {
                  type: 'geojson',
                  data: sharedData,
              }
            : {
                  type: 'geojson',
                  data: {
                      type: 'Feature',
                      geometry: sharedData.geometry,
                      properties: {},
                  },
              };

    return {
        version: 8,
        metadata: {
            id: 'sharedDataGeometry',
        },
        sources: {
            source,
        },
        layers: [
            {
                id: 'fill',
                type: 'fill',
                source: 'source',
                filter: [
                    'has',
                    ['geometry-type'],
                    [
                        'literal',
                        {
                            Polygon: true,
                            MultiPolygon: true,
                        },
                    ],
                ],
                paint: {
                    'fill-color': theme.palette.primary.main,
                    'fill-opacity': 0.5,
                },
            },
            ...styleLayers,
        ],
    };
}
